// Element + region tracking. Pure. No React, no Three.js. Reads the
// live mesh map plus a catalog lookup; returns derived data so other
// subsystems (burrow / submerge eligibility, region-gated movement,
// pip overlays) all share one source of truth.
//
// ── Sorcery rules (codex "Location", "Region", "Site", "Void",
//                   "Underwater", "Underground", "Rubble", "Flooded") ──
//
//   • A location is defined by its square AND region.
//   • A void square has a single location: void.
//   • A site square has two locations: surface + subsurface.
//       - Water site subsurface → underwater
//       - Land site subsurface  → underground
//   • Rubble is an Ordinary site token with the ability "Provides no
//     mana" and no affinity. Per codex: "In all other ways, Rubble is
//     treated the same as any other site. For example, because it has
//     no water affinity, it is a land site." So Rubble exposes both
//     surface AND underground regions like any other land site, and
//     Burrowing minions can be burrowed on it.
//   • A site with at least one water affinity (W) is a water site.
//     A site can be both a water site AND another element (Humble
//     Village flooded → earth site AND water site).
//   • Flooded. Keyword ability granting a minimum of 1 W. A flooded
//     site is always a water site.
//
// ── API surface ──
//   elementsAtCell(meshes, cellAt, col, row, getFullCard)
//     → { air, earth, fire, water } summed across every Site mesh in
//       the cell. Flooded sites are floored to a minimum of 1 water.
//   regionsAtCell(...)
//     → Set<'void' | 'surface' | 'underground' | 'underwater'>
//   isFloodedAtCell(...)
//     → true if any site in the cell carries the Flooded ability.
//   isWaterSiteAtCell / isLandSiteAtCell(...)
//     → boolean shortcuts derived from elementsAtCell.
//   cellsByRegion(meshes, cellAt, totalCols, totalRows, getFullCard)
//     → Map<'col,row', Set<region>>. Bulk pre-computation for
//       movement / placement checks that need O(1) lookups.

// A site's cell: the authoritative server stamp when present, else
// resolved from the mesh's world position. Mesh positions are pure
// presentation and drift while the cell-focus fan is open (a fanned
// site glides over neighbouring squares), so the stamp must win or the
// site's affinity would follow the animation to the wrong cell.
function siteCell(card, mesh, cellAt) {
  const cell = card?.cell;
  if (cell && typeof cell.col === 'number' && typeof cell.row === 'number') return cell;
  return cellAt(mesh.position.x, mesh.position.z);
}

// Pull every Site cardInstance currently in cell (col, row). Returns
// an array of { card, fullCard } pairs.
function sitesInCell(meshes, cellAt, col, row, getFullCard) {
  const sites = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cell = siteCell(card, mesh, cellAt);
    if (!cell || cell.col !== col || cell.row !== row) continue;
    sites.push({ card, fullCard: getFullCard(card.cardId) });
  }
  return sites;
}

// True if `card` carries the Flooded ability. Either as a printed
// keyword OR an aura-granted status (codex: "Since Flooded is an
// ability, effects that remove abilities from sites will also remove
// Flooded"). Manual radial-menu toggle counts too for sandbox; the
// shared keywordQuery helper honours `_suppressedKeywords` so a
// printed Flooded can also be turned off through the menu.
import { hasKeyword } from './keywordQuery';
function hasFloodedAbility(card) { return hasKeyword(card, 'flooded'); }

/**
 * Effective per-site threshold contribution. Single source of truth
 * for every "what does this site provide?" query, so `elementsAtCell`
 * and GameBoard's affinity HUD tracker agree on aura modifications.
 *
 * Combines:
 *   • Printed thresholds from the catalog
 *   • `card._thresholdMultiplier`. Sow the Earth's "double threshold"
 *   • `card._thresholdSuppression`. Drought / Atlantean Fate suppress
 *   • `card._providesThisTurn`. Genesis "Provides (E) this turn"
 *     (Desert Bloom) grants temporary site affinity for the turn it's
 *     played, so the site counts as that element while the grant lives.
 *   • Flooded ability. Minimum 1 water (printed keyword OR aura-granted)
 *
 * Suppression takes precedence over the multiplier: a suppressed
 * element stays at 0 even with a 2× multiplier.
 *
 * @param {object} card     live site instance
 * @param {object} fullCard catalog row
 * @returns {{ air: number, earth: number, fire: number, water: number }}
 */
export function effectiveSiteThresholds(card, fullCard) {
  const totals = { air: 0, earth: 0, fire: 0, water: 0 };
  if (!fullCard) return totals;
  // A `no_threshold` status (Sinterfee's silenced site) blanks every element
  // for as long as the status lives; the per-element suppression bag stays
  // for the aura-derived cases (Drought / Atlantean Fate).
  const sup = (card?.statuses || []).includes('no_threshold')
    ? { air: true, earth: true, fire: true, water: true }
    : (card?._thresholdSuppression || null);
  const mult = card?._thresholdMultiplier || 1;
  const addPrinted = (key, value) => {
    if (sup && sup[key]) return;
    totals[key] += (value || 0) * mult;
  };
  addPrinted('air',   fullCard.airThreshold);
  addPrinted('earth', fullCard.earthThreshold);
  addPrinted('fire',  fullCard.fireThreshold);
  addPrinted('water', fullCard.waterThreshold);
  // Genesis "Provides (A)(E)(W) this turn" (Desert Bloom) stamps the
  // site instance with `_providesThisTurn` while the grant lives. That
  // affinity is the site's own, so it feeds the per-cell element /
  // region classification (an earth site for the Geomancer's Rubble
  // bonus). Not just the controller's casting pool. Suppression still
  // wins over a granted element.
  // Two provides bags feed the site's own affinity: the this-turn grant
  // (Desert Bloom / Annual Fair, cleared at turn end) and the PERMANENT grant
  // (Valley of Delight's "This site provides that permanently." Cleared only
  // when the site leaves play). Both are the site's own affinity, so they flow
  // into per-cell element/region classification AND the controller's casting
  // pool (applyPoolTotals sums effectiveSiteThresholds), which is why the grant
  // is NOT also mirrored into a ledger entry. That would double-count.
  for (const provides of [card?._providesThisTurn, card?._providesPermanent]) {
    if (!provides) continue;
    for (const key of ['air', 'earth', 'fire', 'water']) {
      if (sup && sup[key]) continue;
      totals[key] += (provides[key] || 0) * mult;
    }
  }
  // Flooded grants a MINIMUM of one water affinity, not a +1 bonus
  // (codex Flooded). A site that already provides water keeps its
  // value; only a dry site is floored up to 1.
  if (hasFloodedAbility(card) && !(sup && sup.water)) {
    totals.water = Math.max(totals.water, 1);
  }
  return totals;
}

/**
 * Stamp a site instance with the affinity from a Genesis "Provides
 * (A)(E)(W) this turn" grant (Desert Bloom), so `effectiveSiteThresholds`
 * counts it toward the site's own per-cell elements/regions for the turn.
 * Accumulates across multiple grants; cleared at turn end alongside the
 * matching `this turn` ledger entry.
 *
 * @param {object} card live site instance (mutated)
 * @param {{ air?: number, earth?: number, fire?: number, water?: number }} args grant element amounts
 */
export function stampSiteProvidesThisTurn(card, args) {
  if (!card || !args) return;
  const base = card._providesThisTurn || { air: 0, earth: 0, fire: 0, water: 0 };
  card._providesThisTurn = {
    air:   base.air   + (args.air   || 0),
    earth: base.earth + (args.earth || 0),
    fire:  base.fire  + (args.fire  || 0),
    water: base.water + (args.water || 0),
  };
}

/**
 * Stamp a site with a PERMANENT affinity grant (Valley of Delight's Genesis
 * "This site provides that permanently"). Unlike the this-turn stamp this
 * survives the turn boundary; it's cleared only when the site leaves play
 * (resetCardOnLeavePlay), which is the grant's source-alive check. A destroyed
 * Valley provides nothing.
 *
 * @param {object} card live site instance (mutated)
 * @param {{ air?: number, earth?: number, fire?: number, water?: number }} args grant element amounts
 */
export function stampSiteProvidesPermanent(card, args) {
  if (!card || !args) return;
  const base = card._providesPermanent || { air: 0, earth: 0, fire: 0, water: 0 };
  card._providesPermanent = {
    air:   base.air   + (args.air   || 0),
    earth: base.earth + (args.earth || 0),
    fire:  base.fire  + (args.fire  || 0),
    water: base.water + (args.water || 0),
  };
}

/**
 * Clear the per-turn "provides this turn" grant from every site in the
 * mesh map. Called at end of turn when the matching ledger entries are
 * pruned, so the temporary affinity expires in lockstep.
 */
export function clearProvidesThisTurn(meshes) {
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (card && card._providesThisTurn) {
      card._providesThisTurn = null;
      card._siteProvidesStamped = false;
    }
  }
}

export function elementsAtCell(meshes, cellAt, col, row, getFullCard) {
  const totals = { air: 0, earth: 0, fire: 0, water: 0 };
  for (const { card, fullCard } of sitesInCell(meshes, cellAt, col, row, getFullCard)) {
    const eff = effectiveSiteThresholds(card, fullCard);
    totals.air   += eff.air;
    totals.earth += eff.earth;
    totals.fire  += eff.fire;
    totals.water += eff.water;
  }
  return totals;
}

export function isFloodedAtCell(meshes, cellAt, col, row, getFullCard) {
  for (const { card } of sitesInCell(meshes, cellAt, col, row, getFullCard)) {
    if (hasFloodedAbility(card)) return true;
  }
  return false;
}

export function isWaterSiteAtCell(meshes, cellAt, col, row, getFullCard) {
  // "A flooded site has a minimum of one water affinity (W). Therefore
  // it's a water site." So any cell with water affinity > 0 OR an
  // explicit Flooded ability counts as a water site.
  const elements = elementsAtCell(meshes, cellAt, col, row, getFullCard);
  return elements.water > 0;
}

export function isLandSiteAtCell(meshes, cellAt, col, row, getFullCard) {
  // A site that is NOT a water site is a land site (per codex Rubble
  // entry: "because it has no water affinity, it is a land site").
  // A cell with no site is neither water nor land. It's void.
  const sites = sitesInCell(meshes, cellAt, col, row, getFullCard);
  if (sites.length === 0) return false;
  return !isWaterSiteAtCell(meshes, cellAt, col, row, getFullCard);
}

export function regionsAtCell(meshes, cellAt, col, row, getFullCard) {
  const out = new Set();
  const sites = sitesInCell(meshes, cellAt, col, row, getFullCard);
  if (sites.length === 0) {
    out.add('void');
    return out;
  }
  // Any site exposes surface. Subsurface depends on EFFECTIVE water
  // affinity. Driven through `effectiveSiteThresholds` so aura-driven
  // suppression (Drought) and multiplier (Sow the Earth) propagate
  // into the body-of-water/underground classification just like they
  // do for the playmat pips and HUD affinity tracker.
  out.add('surface');
  for (const { card, fullCard } of sites) {
    const eff = effectiveSiteThresholds(card, fullCard);
    if (eff.water > 0) out.add('underwater');
    else out.add('underground');
  }
  return out;
}

// Bulk lookup. Useful for movement / placement scans that need to
// query every cell in one pass. Returns Map<'col,row', Set<region>>.
export function cellsByRegion(meshes, cellAt, totalCols, totalRows, getFullCard) {
  const out = new Map();
  for (let c = 0; c < totalCols; c++) {
    for (let r = 0; r < totalRows; r++) {
      out.set(`${c},${r}`, regionsAtCell(meshes, cellAt, c, r, getFullCard));
    }
  }
  return out;
}

// Cells that have at least one Site (any kind). Useful as a primary
// driver for the pip-overlay React render. Only emits a DOM node
// where there's something to display.
export function cellsWithAnySite(meshes, cellAt) {
  const out = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cell = siteCell(card, mesh, cellAt);
    if (cell) out.add(`${cell.col},${cell.row}`);
  }
  return out;
}

/**
 * Connected components of cells that share the same subsurface region
 * via 4-cardinal adjacency. v1.72. Drives the region-view highlight:
 * a "body of water" is a set of adjacent cells that ALL expose
 * 'underwater' (so a Submerged minion can traverse them without
 * surfacing). Same logic for underground (Burrowed). Each component
 * is returned as a Set of "col,row" strings.
 *
 * @param region 'underwater' | 'underground'. Which subsurface to scan
 * @returns Array<Set<string>> one Set per connected component
 */
export function connectedSubsurfaceComponents(
  meshes, cellAt, totalCols, totalRows, getFullCard, region,
) {
  if (region !== 'underwater' && region !== 'underground') return [];
  // Build the per-cell membership map once.
  const member = new Set();
  for (let c = 0; c < totalCols; c++) {
    for (let r = 0; r < totalRows; r++) {
      const regions = regionsAtCell(meshes, cellAt, c, r, getFullCard);
      if (regions.has(region)) member.add(`${c},${r}`);
    }
  }
  // BFS each unvisited cell to extract its component.
  const visited = new Set();
  const components = [];
  const deltas = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  for (const key of member) {
    if (visited.has(key)) continue;
    const component = new Set();
    const queue = [key];
    visited.add(key);
    while (queue.length > 0) {
      const cur = queue.shift();
      component.add(cur);
      const [colStr, rowStr] = cur.split(',');
      const col = parseInt(colStr, 10);
      const row = parseInt(rowStr, 10);
      for (const [dc, dr] of deltas) {
        const nKey = `${col + dc},${row + dr}`;
        if (!member.has(nKey) || visited.has(nKey)) continue;
        visited.add(nKey);
        queue.push(nKey);
      }
    }
    components.push(component);
  }
  return components;
}

export function siteResistsChanges(card, fullCard) {
  return (fullCard?.automation || []).some(e => e.trigger === 'static' && e.effect?.some(op => op.op === 'prevent_site_changes'));
}
